<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import { ref } from 'vue';
import { QrcodeStream, type DetectedBarcode } from 'vue-qrcode-reader';
import router from '@/router';
import { isValidCommissioningQRCode } from '../utils/solshare-commissioning';
import { message } from 'ant-design-vue';

const error = ref('');
const loading = ref(true);

const onReady = () => {
  loading.value = false;
};

const onDetect = (detectedCodes: DetectedBarcode[]) => {
  const decodedString = detectedCodes[0].rawValue; // The lib will read all presented qr codes, for our usecase we assume there is only one.

  if (isValidCommissioningQRCode(decodedString)) {
    const serialNumber = decodedString.substring(decodedString.lastIndexOf('/') + 1);

    router.replace({
      name: 'SearchingSolshare',
      params: { serialNumber },
    });
  } else {
    message.error('Invalid QR code');
  }
};

const onError = (err: unknown) => {
  if (err && typeof err === 'object' && 'name' in err) {
    const errorObj = err as { name: string };
    if (errorObj.name === 'NotAllowedError') {
      error.value = 'ERROR: You need to grant camera access permisson';
    } else if (errorObj.name === 'NotFoundError') {
      error.value = 'ERROR: No camera on this device';
    } else if (errorObj.name === 'NotSupportedError') {
      error.value = 'ERROR: Secure context required (HTTPS, localhost)';
    } else if (errorObj.name === 'NotReadableError') {
      error.value = 'ERROR: Is the camera already in use?';
    } else if (errorObj.name === 'OverconstrainedError') {
      error.value = 'ERROR: Installed cameras are not suitable';
    } else if (errorObj.name === 'StreamApiNotSupportedError') {
      error.value = 'ERROR: Stream API is not supported in this browser';
    }
  }

  loading.value = false;
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page">
    <template #title>Scan the serial number.</template>
    <template #subTitle> Allow your phone to open camera and scan QR code here. </template>
    <div class="content">
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      <qrcode-stream @camera-on="onReady" @detect="onDetect" @error="onError">
        <div v-if="loading" class="loading-indicator">
          <LoadingSpinner />
          <p>Opening Camera...</p>
        </div>
      </qrcode-stream>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.error-message {
  color: @color-error;
}

.loading-indicator {
  text-align: center;
}
</style>
